import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Title } from "react-admin";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";
import { CourseChoices } from "utils/choices";
import { EDUCATION_STUDENTS, STUDENTS_PATH } from "utils/constants";
import BackButtonToolbar from "features/BackButtonToolbar";
import DisciplineRow from "./DisciplineRow";
import { isCourseScheduled } from "./utils";

const STUDENTS_LIST_PATH = `/${STUDENTS_PATH}/${EDUCATION_STUDENTS}`;

const columnHeaders = [
  { label: "Дисциплина", md: 2 },
  { label: "Кредити (уч. план)", md: 0.75 },
  { label: "Лекции (уч. план)", md: 0.75 },
  { label: "Упражнения (уч. план)", md: 0.75 },
  { label: "Кредити (пред. у-т)", md: 0.75 },
  { label: "Лекции (пред. у-т)", md: 0.75 },
  { label: "Упражнения (пред. у-т)", md: 0.75 },
  { label: "Извънауд. заетост", md: 0.75 },
  { label: "Статус", md: 1.25 },
  { label: "Оценка", md: 0.75 },
  { label: "Коментар", md: 1.25 },
  { label: "Действия", md: 1.5 },
];

const orderInCourse = group => group.semester ?? Number.MAX_SAFE_INTEGER;

// Cyclic disciplines have no semester, so each course gets its own group right after its semesters.
const groupRows = rows => {
  const groups = new Map();
  rows.forEach(row => {
    const byCourse = isCourseScheduled(row);
    const key = byCourse ? `course-${row.course}` : `semester-${row.semester}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        course: row.course,
        semester: row.semester,
        title: byCourse ? `Курс ${row.course} – циклични дисциплини` : `Семестър ${row.semester}`,
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  });
  return [...groups.values()].sort((a, b) => a.course - b.course || orderInCourse(a) - orderInCourse(b));
};

const IndividualCurriculumPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const notifyError = useNotifyError();

  const { educationId, facultyNumber, curriculumName, course } = location?.state ?? {};

  const [rows, setRows] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getIndividualCurriculum(educationId),
        null,
        "GET",
      );
      setRows(data ?? []);
    } catch (err) {
      notifyError(err);
      navigate(STUDENTS_LIST_PATH);
    }
  }, [educationId, notifyError, navigate]);

  useEffect(() => {
    if (!educationId) {
      navigate(STUDENTS_LIST_PATH);
      return;
    }
    fetchRows();
  }, [educationId, fetchRows, navigate]);

  if (!educationId) return null;

  const groups = groupRows(rows ?? []);
  const courseName = CourseChoices.find(choice => choice.id === course)?.name;

  return (
    <Box sx={{ width: "100%", overflow: "hidden", pb: 3 }}>
      <Title title="Индивидуален учебен план" />
      <BackButtonToolbar />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 3 }}>
        <Typography>
          Факултетен номер: <strong>{facultyNumber ?? "-"}</strong>
        </Typography>
        <Typography>
          Учебен план: <strong>{curriculumName ?? "-"}</strong>
        </Typography>
        <Typography>
          Курс: <strong>{courseName ?? "-"}</strong>
        </Typography>
      </Box>
      {groups.map((group, idx) => (
        <Accordion
          key={group.key}
          defaultExpanded={idx === 0}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>{group.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" }, mb: 1, fontWeight: "bold" }}
            >
              {columnHeaders.map(header => (
                <Grid
                  item
                  key={header.label}
                  md={header.md}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {header.label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {group.rows.map(row => (
              <DisciplineRow
                key={row.disciplineId}
                educationId={educationId}
                row={row}
                onSaved={fetchRows}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default IndividualCurriculumPage;
